import React from "react";
import { ArrowUpRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";
import { Reveal } from "./Reveal";

const FAQS = [
  {
    q: "How long does a project usually take?",
    a: "Timelines vary depending on the project. Brand Identity projects typically take 2–3 weeks, while larger branding or website projects may take 3–6 weeks. A detailed timeline is shared before every project begins.",
  },
  {
    q: "What is your design process?",
    a: "Every project follows a structured approach: Discovery → Strategy → Concept Development → Design → Feedback → Final Delivery. This ensures every design decision aligns with your business goals and creates lasting value.",
  },
  {
    q: "How many revisions are included?",
    a: "Reasonable revisions are included throughout the design process to ensure the final outcome aligns with your vision. The exact number depends on the scope outlined in the project proposal.",
  },
  {
    q: "Do you offer ongoing design support?",
    a: "Absolutely. Many of my clients choose to continue working with me through monthly creative retainers for ongoing branding, marketing assets, presentations, websites, and design support.",
  },
  {
    q: "What files will I receive after project completion?",
    a: "You'll receive all final assets in industry-standard formats, along with editable source files where applicable and files optimized for both digital and print use.",
  },
  {
    q: "How do we get started?",
    a: "Simply reach out via email with a brief overview of your project. We'll discuss your goals, timeline, and requirements before I prepare a tailored proposal.",
  },
  {
    q: "Why should I choose to work with you?",
    a: "With over 11 years of experience, I combine strategic thinking with thoughtful design to create brands that not only look exceptional but also support real business growth. My focus is on building long-term partnerships and delivering creative solutions that are purposeful, memorable, and results-driven.",
  },
];

export const FAQ = () => {
  return (
    <section id="faq" className="py-24 md:py-32 lg:py-40" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">FAQ</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-charcoal">
              Good to <span className="italic text-burgundy">know.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-3xl border border-charcoal/10 bg-white/50 backdrop-blur-sm p-7" data-testid="faq-still-question">
              <h3 className="font-heading text-2xl text-charcoal">Still have a question?</h3>
              <p className="mt-3 text-sm text-charcoal/60 leading-relaxed">
                If you don't see your question here, feel free to get in touch. I'd be happy to
                discuss your project and help you find the right solution.
              </p>
              <button
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="group mt-5 inline-flex items-center gap-2 rounded-full bg-charcoal text-cream hover:bg-burgundy px-6 py-3 text-sm font-medium tracking-wide transition-colors duration-300"
                data-testid="faq-contact-button"
              >
                Get in touch
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-charcoal/10"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-heading text-xl md:text-2xl text-charcoal hover:text-burgundy hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-charcoal/70 leading-relaxed pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
